import type { AppProps } from 'next/app';
import { Montserrat } from 'next/font/google';
import Script from 'next/script';
import { useEffect, useRef } from 'react';
import { useTernaryDarkMode } from 'usehooks-ts';
import ErrorBoundary from '../components/ErrorBoundary';
import '../styles/globals.css';
import { ADSENSE_ID, DARK_MODE_SETTINGS, GTM_ID } from '../utils/constants';

const montserrat = Montserrat({
  weight: ['400', '700'],
  subsets: ['latin'],
});

export default function App({ Component, pageProps, router }: AppProps) {
  const { isDarkMode } = useTernaryDarkMode(DARK_MODE_SETTINGS);

  // The theme is first set before the page is drawn (see _document.tsx). The
  // first render here hasn't read the saved theme yet, so it's skipped.
  const themeReady = useRef(false);
  useEffect(() => {
    if (!themeReady.current) {
      themeReady.current = true;
      return;
    }
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  // Offline support and installing as an app (see public/sw.js). Production
  // only, so development always uses fresh files.
  useEffect(() => {
    if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(() => {});
    }
  }, []);

  // Embedded keyboards (on other sites) have no ads, and so no consent message
  const embed = router.pathname.startsWith('/embed/');

  return (
    <>
      {/* Ads and analytics load once the page has loaded and the browser is idle,
          so they don't slow down the first taps and keypresses. The consent
          defaults they need are set first, in _document.tsx. */}
      <Script id="gtm" strategy="lazyOnload">
        {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${GTM_ID}');`}
      </Script>
      {!embed && (
        <Script
          src={`https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_ID}`}
          strategy="lazyOnload"
          crossOrigin="anonymous"
        />
      )}
      <div className={montserrat.className}>
        <ErrorBoundary>
          {/* Keyed by path so each keyboard page starts with fresh state */}
          <Component key={router.asPath} {...pageProps} />
        </ErrorBoundary>
      </div>
    </>
  );
}
